import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Items currently selected for try-on (UI state only)
  selectedForTryOn: [],
  // Active items being worn in current generation (UI state only)
  activeItems: [],
  // UI filters and preferences
  filters: {
    category: 'all',
    sortBy: 'createdAt:desc',
    searchTerm: ''
  },
  // UI state for selections
  multiSelectMode: false,
  selectedItemIds: []
};

const clothingItemsSlice = createSlice({
  name: 'clothingItems',
  initialState,
  reducers: {
    // Add item to try-on selection
    addToTryOn: (state, action) => {
      const item = action.payload;
      console.log('[Clothing] Redux addToTryOn:', {
        itemId: item.id,
        itemName: item.clothesName || item.name,
        currentCount: state.activeItems.length,
        alreadyExists: state.selectedForTryOn.includes(item.id)
      });
      if (!state.selectedForTryOn.includes(item.id)) {
        state.selectedForTryOn.push(item.id);
        state.activeItems.push(item);
        console.log('[Clothing] Item added, new count:', state.activeItems.length);
      } else {
        console.log('[Clothing] Item already in wearing list, skipping');
      }
    },
    
    // Remove item from try-on selection
    removeFromTryOn: (state, action) => {
      const itemId = action.payload;
      state.selectedForTryOn = state.selectedForTryOn.filter(id => id !== itemId);
      state.activeItems = state.activeItems.filter(item => item.id !== itemId);
    },
    
    // Clear all try-on selections
    clearTryOnItems: (state) => {
      state.selectedForTryOn = [];
      state.activeItems = [];
    },
    
    // Set active items (used when loading from history or VTO restoration)
    setActiveItems: (state, action) => {
      const items = action.payload || [];
      console.log('[Clothing] Redux setActiveItems (REPLACES all):', {
        newCount: items.length,
        newItems: items.map(i => ({ id: i.id, name: i.clothesName || i.name })),
        previousCount: state.activeItems.length
      });
      state.activeItems = items;
      state.selectedForTryOn = items.map(item => item.id);
    },
    
    // Update filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Set search term
    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload;
    },
    
    // Set sort order
    setSortBy: (state, action) => {
      state.filters.sortBy = action.payload;
    },
    
    // Set category filter
    setCategoryFilter: (state, action) => {
      state.filters.category = action.payload;
    },
    
    // Multi-select mode for bulk operations
    toggleMultiSelectMode: (state) => {
      state.multiSelectMode = !state.multiSelectMode;
      if (!state.multiSelectMode) {
        state.selectedItemIds = [];
      }
    },
    
    // Toggle item selection in multi-select mode
    toggleItemSelection: (state, action) => {
      const itemId = action.payload;
      if (state.selectedItemIds.includes(itemId)) {
        state.selectedItemIds = state.selectedItemIds.filter(id => id !== itemId);
      } else {
        state.selectedItemIds.push(itemId);
      }
    },
    
    // Select all items
    selectAllItems: (state, action) => {
      const allItemIds = action.payload || [];
      state.selectedItemIds = allItemIds;
    },
    
    // Clear all selections
    clearSelections: (state) => {
      state.selectedItemIds = [];
    },
    
    // Remove items from active/selected when they're deleted (cleanup)
    cleanupDeletedItems: (state, action) => {
      const deletedItemIds = Array.isArray(action.payload) ? action.payload : [action.payload];
      state.selectedForTryOn = state.selectedForTryOn.filter(id => !deletedItemIds.includes(id));
      state.activeItems = state.activeItems.filter(item => !deletedItemIds.includes(item.id));
      state.selectedItemIds = state.selectedItemIds.filter(id => !deletedItemIds.includes(id));
    },

    // Reset to initial state
    resetClothingItemsState: (state) => {
      Object.assign(state, initialState);
    }
  }
});

export const {
  addToTryOn,
  removeFromTryOn,
  clearTryOnItems,
  setActiveItems,
  setFilters,
  setSearchTerm,
  setSortBy,
  setCategoryFilter,
  toggleMultiSelectMode,
  toggleItemSelection,
  selectAllItems,
  clearSelections,
  cleanupDeletedItems,
  resetClothingItemsState
} = clothingItemsSlice.actions;

// Selectors for UI state only (data comes from RTK Query)
export const selectSelectedForTryOn = (state) => state.clothingItems.selectedForTryOn;
export const selectActiveItems = (state) => state.clothingItems.activeItems;
export const selectHasActiveItems = (state) => state.clothingItems.activeItems.length > 0;
export const selectClothingFilters = (state) => state.clothingItems.filters;
export const selectSearchTerm = (state) => state.clothingItems.filters.searchTerm;
export const selectSortBy = (state) => state.clothingItems.filters.sortBy;
export const selectCategoryFilter = (state) => state.clothingItems.filters.category;
export const selectMultiSelectMode = (state) => state.clothingItems.multiSelectMode;
export const selectSelectedItemIds = (state) => state.clothingItems.selectedItemIds;

// Helper selectors that work with RTK Query data
export const selectActiveItemsWithData = (state) => {
  // This will be used by components that need the full item data
  // Components should use RTK Query hooks to get the actual data
  return state.clothingItems.activeItems;
};

// Selector to check if an item is selected for try-on
export const selectIsItemSelectedForTryOn = (state, itemId) => 
  state.clothingItems.selectedForTryOn.includes(itemId);

// Selector to check if an item is selected in multi-select mode
export const selectIsItemSelected = (state, itemId) => 
  state.clothingItems.selectedItemIds.includes(itemId);

export default clothingItemsSlice.reducer;